import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useReabrirOS } from '@/hooks/useOrdensServico';
import { OrdemServico, getStatusOSLabels } from '@/types/ordemServico';
import { Loader2, RotateCcw, ArrowRight } from 'lucide-react';

interface ReabrirOSDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  os: OrdemServico;
}

export function ReabrirOSDialog({ open, onOpenChange, os }: ReabrirOSDialogProps) {
  const { t } = useTranslation();
  const STATUS_OS_LABELS = getStatusOSLabels(t);
  const [motivo, setMotivo] = useState('');
  const reabrirOS = useReabrirOS();

  const handleReabrir = async () => {
    await reabrirOS.mutateAsync({
      id: os.id,
      motivo: motivo.trim(),
    });
    setMotivo('');
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setMotivo('');
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            {t('orders.reopen.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                <span dangerouslySetInnerHTML={{ __html: t('orders.reopen.description', { number: os.numero_os }) }} />
              </p>
              
              <div className="flex items-center justify-center gap-3 p-3 bg-muted rounded-lg">
                <Badge variant="secondary">
                  {STATUS_OS_LABELS[os.status]}
                </Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="default">
                  {t('orders.status.pending')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground text-center mt-2">
                <span dangerouslySetInnerHTML={{ __html: t('orders.reopen.info') }} />
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motivo">{t('orders.reopen.reasonLabel')}</Label>
            <Textarea
              id="motivo"
              placeholder={t('orders.reopen.reasonPlaceholder')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={reabrirOS.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button
            onClick={handleReabrir}
            disabled={reabrirOS.isPending || motivo.trim() === ''}
          >
            {reabrirOS.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('orders.reopen.reopening')}
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('orders.reopen.confirm')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
